import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from '@/race-tracker/components/ui/button';
import { Plus, Download } from 'lucide-react';
import { Radio, MapPin, Map, Building2, Home, UserX, PhoneOff } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/race-tracker/components/ui/alert-dialog';
import { useStations, useRepFirmManagers, useCreateStation, useUpdateStation, useDeleteStation, useCreateChangeLog } from '@/big-political-list/hooks/useBplData';
import { getAllBaseStates, getDistinctValues, countByField, stateMatchesFilter, stationMatchesSearch, computeFieldChanges, STATION_FIELDS } from '@/big-political-list/lib/stationUtils';
import StatCard from '@/big-political-list/components/shared/StatCard';
import FilterChips from '@/big-political-list/components/shared/FilterChips';
import DistributionBar from '@/big-political-list/components/shared/DistributionBar';
import StationFilters from '@/big-political-list/components/stations/StationFilters';
import StationTable from '@/big-political-list/components/stations/StationTable';
import StationDetailDrawer from '@/big-political-list/components/stations/StationDetailDrawer';
import StationFormModal from '@/big-political-list/components/stations/StationFormModal';

import { getBplUserName } from '@/big-political-list/lib/bplUser'
import BplQueryError from '@/big-political-list/components/shared/BplQueryError';
import { BplStatCardsSkeleton, BplTableSkeleton, BplUpdatingBanner } from '@/big-political-list/components/shared/BplSkeleton';

export default function BigPoliticalList({ canWrite = true, user, profile }) {
  const {
    data: stations = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isLoadingMore,
    loadProgressLabel,
  } = useStations();
  const { data: managers = [] } = useRepFirmManagers();
  const createStation = useCreateStation();
  const updateStation = useUpdateStation();
  const deleteStation = useDeleteStation();
  const createLog = useCreateChangeLog();

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [selectedStation, setSelectedStation] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editStation, setEditStation] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (hasNextPage && !isLoadingMore) {
      fetchNextPage()
    }
  }, [hasNextPage, isLoadingMore, fetchNextPage])

  const showInitialSkeleton = isLoading && stations.length === 0
  const showRefreshing = isFetching && stations.length > 0 && !isLoadingMore

  // Derived data
  const baseStates = useMemo(() => getAllBaseStates(stations), [stations]);
  const markets = useMemo(() => getDistinctValues(stations, 'market'), [stations]);
  const affiliates = useMemo(() => getDistinctValues(stations, 'affiliate'), [stations]);
  const repFirms = useMemo(() => getDistinctValues(stations, 'rep_firm'), [stations]);
  const ownershipDist = useMemo(() => countByField(stations, 'ownership', 6), [stations]);
  const affiliateDist = useMemo(() => countByField(stations, 'affiliate', 6), [stations]);

  // Health counts
  const missingAddress = useMemo(() => stations.filter(s => !s.station_address).length, [stations]);
  const missingCD = useMemo(() => stations.filter(s => !s.cd_contact_name).length, [stations]);
  const missingRep = useMemo(() => stations.filter(s => !s.rep_email || !s.rep_phone).length, [stations]);

  // Filter logic
  const filteredStations = useMemo(() => {
    return stations.filter(s => {
      if (!stationMatchesSearch(s, search)) return false;
      if (filters.State && !stateMatchesFilter(s.state, filters.State)) return false;
      if (filters.Market && s.market !== filters.Market) return false;
      if (filters.Affiliate && s.affiliate !== filters.Affiliate) return false;
      if (filters['Rep Firm'] && s.rep_firm !== filters['Rep Firm']) return false;
      if (filters.Ownership && s.ownership !== filters.Ownership) return false;
      if (filters['Missing address'] && !!s.station_address) return false;
      if (filters['Missing C&D'] && !!s.cd_contact_name) return false;
      if (filters['Missing rep contact'] && !(!s.rep_email || !s.rep_phone)) return false;
      return true;
    });
  }, [stations, search, filters]);

  const setFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);
  const removeFilter = useCallback((key) => {
    setFilters(prev => { const n = { ...prev }; delete n[key]; return n; });
  }, []);
  const clearFilters = useCallback(() => setFilters({}), []);

  const handleHealthClick = useCallback((filterKey, label) => {
    setFilters(prev => {
      if (prev[filterKey]) {
        const n = { ...prev };
        delete n[filterKey];
        return n;
      }
      return { ...prev, [filterKey]: label };
    });
  }, []);

  const handleRowClick = useCallback((station) => {
    setSelectedStation(station);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((station) => {
    setEditStation(station);
    setFormOpen(true);
    setDrawerOpen(false);
  }, []);

  const handleAddNew = useCallback(() => {
    setEditStation(null);
    setFormOpen(true);
  }, []);

  const userName = useMemo(() => getBplUserName(profile, user), [profile, user]);

  const handleSave = useCallback(async (formData) => {
    setSaving(true);
    if (editStation?.id) {
      const changes = computeFieldChanges(editStation, formData, STATION_FIELDS);
      await updateStation.mutateAsync({ id: editStation.id, data: formData });
      if (changes.length > 0) {
        await createLog.mutateAsync({
          user: userName, action: 'updated', entity_type: 'Station',
          record_label: `${formData.station} — ${formData.market}`,
          field_changes: changes,
        });
      }
    } else {
      await createStation.mutateAsync(formData);
      await createLog.mutateAsync({
        user: userName, action: 'created', entity_type: 'Station',
        record_label: `${formData.station} — ${formData.market}`,
      });
    }
    setSaving(false);
    setFormOpen(false);
    setEditStation(null);
  }, [editStation, updateStation, createStation, createLog, userName]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await deleteStation.mutateAsync(deleteTarget.id);
    await createLog.mutateAsync({
      user: userName, action: 'deleted', entity_type: 'Station',
      record_label: `${deleteTarget.station} — ${deleteTarget.market}`,
    });
    setDeleteTarget(null);
    setDrawerOpen(false);
  }, [deleteTarget, deleteStation, createLog, userName]);

  const handleExport = useCallback(async (filtered = false) => {
    const data = filtered ? filteredStations : stations;
    const headers = STATION_FIELDS;
    const csv = [headers.join(','), ...data.map(row => headers.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `big_political_list${filtered ? '_filtered' : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredStations, stations]);

  if (isError && stations.length === 0) {
    return <BplQueryError label="stations" error={error} onRetry={refetch} />;
  }

  return (
    <div className="space-y-5">
      <BplUpdatingBanner
        message={
          loadProgressLabel ||
          (showRefreshing ? 'Refreshing stations…' : null)
        }
      />

      {/* Coverage Cards */}
      {showInitialSkeleton ? (
        <BplStatCardsSkeleton count={4} />
      ) : (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Total Reps" value={stations.length} icon={Radio} />
        <StatCard title="DMAs Covered" value={new Set(stations.map(s => s.market)).size} icon={Map} />
        <StatCard title="States Covered" value={baseStates.length} icon={MapPin} />
        <StatCard title="Rep Firms" value={repFirms.length} icon={Building2} />
      </div>
      )}

      {showInitialSkeleton ? (
        <>
          <BplStatCardsSkeleton count={3} />
          <div className="h-24 rounded-lg border bg-card animate-pulse bg-muted/30" />
          <BplTableSkeleton rows={10} />
        </>
      ) : (
      <>
      {/* Health Cards */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          title="Missing Station Address"
          value={missingAddress}
          icon={Home}
          variant="health"
          onClick={() => handleHealthClick('Missing address', 'Yes')}
          active={!!filters['Missing address']}
        />
        <StatCard
          title="Missing C&D Contact"
          value={missingCD}
          icon={UserX}
          variant="health"
          onClick={() => handleHealthClick('Missing C&D', 'Yes')}
          active={!!filters['Missing C&D']}
        />
        <StatCard
          title="Missing Rep Contact"
          value={missingRep}
          icon={PhoneOff}
          variant="health"
          onClick={() => handleHealthClick('Missing rep contact', 'Yes')}
          active={!!filters['Missing rep contact']}
        />
      </div>

      {/* Distribution Bars */}
      <div className="grid grid-cols-2 gap-6 p-4 rounded-lg border bg-card">
        <DistributionBar
          title="By Ownership"
          data={ownershipDist}
          total={stations.length}
          onBarClick={v => setFilter('Ownership', filters.Ownership === v ? '' : v)}
          activeValue={filters.Ownership}
        />
        <DistributionBar
          title="By Affiliate"
          data={affiliateDist}
          total={stations.length}
          onBarClick={v => setFilter('Affiliate', filters.Affiliate === v ? '' : v)}
          activeValue={filters.Affiliate}
        />
      </div>

      {/* Actions row */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button onClick={handleAddNew} disabled={!canWrite} className="bg-primary text-primary-foreground hover:bg-primary/90 h-8 text-xs disabled:opacity-50">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Rep
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleExport(false)}>
            <Download className="w-3.5 h-3.5 mr-1" /> Export All
          </Button>
          {Object.keys(filters).length > 0 && (
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => handleExport(true)}>
              <Download className="w-3.5 h-3.5 mr-1" /> Export Filtered
            </Button>
          )}
        </div>
      </div>

      {/* Search & Filters */}
      <StationFilters
        search={search}
        onSearchChange={setSearch}
        filters={filters}
        onFilterChange={setFilter}
        stateOptions={baseStates}
        marketOptions={markets}
        affiliateOptions={affiliates}
        repFirmOptions={repFirms}
      />

      {/* Filter chips */}
      <FilterChips filters={filters} onRemove={removeFilter} onClearAll={clearFilters} />

      {/* Table */}
      <StationTable stations={filteredStations} onRowClick={handleRowClick} />
      </>
      )}

      {/* Drawer */}
      <StationDetailDrawer
        station={selectedStation}
        managers={managers}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onEdit={handleEdit}
        onDelete={(s) => { setDeleteTarget(s); setDrawerOpen(false); }}
        canWrite={canWrite}
      />

      {/* Form Modal */}
      <StationFormModal
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditStation(null); }}
        station={editStation}
        onSave={handleSave}
        saving={saving}
      />

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Station</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteTarget?.station} ({deleteTarget?.market})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}